import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import { SectionHeader, Panel, FieldRow } from "@/components/admin/common";

const TITLE_FIELDS = [
  ["chip", "Section chip"],
  ["title_line1", "Title — line 1"],
  ["title_line2_italic", "Title — italic word"],
  ["title_line2_rest", "Title — rest of line 2"],
  ["title_line3", "Title — line 3 start"],
  ["title_line3_red", "Title — red accent"],
];

const ICON_OPTIONS = [
  { value: "heart", label: "Heart" },
  { value: "leaf", label: "Leaf" },
  { value: "flame", label: "Flame" },
  { value: "users", label: "Users" },
  { value: "star", label: "Star" },
  { value: "sparkles", label: "Sparkles" },
];

export default function AdminAbout({ value, onChange, onSave, onReset, saving }) {
  const v = value || {};
  const pillars = v.pillars || [];
  const update = (k, val) => onChange({ ...v, [k]: val });

  const updatePillar = (idx, patch) => {
    const next = pillars.map((p, i) => (i === idx ? { ...p, ...patch } : p));
    update("pillars", next);
  };
  const addPillar = () =>
    update("pillars", [...pillars, { icon: "sparkles", title: "New pillar", text: "" }]);
  const removePillar = (idx) => {
    if (!window.confirm("Remove this pillar?")) return;
    update(
      "pillars",
      pillars.filter((_, i) => i !== idx),
    );
  };

  return (
    <div>
      <SectionHeader
        title="About section"
        description="Edit your story and the four pillars shown on the public site."
        onSave={() => onSave(v)}
        onReset={onReset}
        saving={saving}
      />
      <div className="space-y-6">
        <Panel>
          <FieldRow>
            {TITLE_FIELDS.map(([k, l]) => (
              <div key={k}>
                <Label>{l}</Label>
                <Input
                  data-testid={`admin-about-${k}`}
                  value={v[k] || ""}
                  onChange={(e) => update(k, e.target.value)}
                  className="h-11 rounded-xl mt-1"
                />
              </div>
            ))}
          </FieldRow>
          <div className="mt-5">
            <Label>Story paragraphs (leave a blank line between paragraphs)</Label>
            <Textarea
              data-testid="admin-about-paragraphs"
              value={v.paragraphs || ""}
              onChange={(e) => update("paragraphs", e.target.value)}
              rows={8}
              className="rounded-xl mt-1"
            />
          </div>
        </Panel>

        <Panel>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-lg">Pillars</h3>
            <Button
              type="button"
              onClick={addPillar}
              variant="outline"
              className="rounded-full h-10"
              data-testid="admin-add-pillar"
            >
              <Plus className="h-4 w-4 mr-2" /> Add pillar
            </Button>
          </div>
          <div className="space-y-4">
            {pillars.map((p, idx) => (
              <div
                key={idx}
                data-testid={`admin-pillar-${idx}`}
                className="rounded-2xl bg-[#FAF8F5] border border-[#E8DED5] p-4 space-y-3"
              >
                <FieldRow cols={3}>
                  <div>
                    <Label>Icon</Label>
                    <Select
                      value={p.icon}
                      onValueChange={(val) => updatePillar(idx, { icon: val })}
                    >
                      <SelectTrigger className="h-11 rounded-xl mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ICON_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label>Title</Label>
                    <Input
                      value={p.title}
                      onChange={(e) => updatePillar(idx, { title: e.target.value })}
                      className="h-11 rounded-xl mt-1"
                    />
                  </div>
                </FieldRow>
                <div>
                  <Label>Text</Label>
                  <Textarea
                    value={p.text}
                    onChange={(e) => updatePillar(idx, { text: e.target.value })}
                    rows={2}
                    className="rounded-xl mt-1"
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => removePillar(idx)}
                    className="text-[#E53935] hover:bg-[#E53935]/10 h-8"
                  >
                    <Trash2 className="h-4 w-4 mr-2" /> Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
